import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'react-i18next';

import { PriceListCollection } from '../../../types/api/priceLists';
import {
  TableColumnHeadersMap,
  TableColumns,
  Unpacked,
} from '../../../types/util';
import { emptyCell, noColumnStyle } from '../../../utils/constants/tables';
import { dateCell } from '../../../utils/helpers/dataTable';
import { withTrailingEllipsis } from '../../../utils/helpers/formatting';
import Tooltip from '../../Misc/Tooltip/Tooltip';
import ActionsTableMenu from '../Components/ActionsTableMenu/ActionsTableMenu';

export type Row = Unpacked<PriceListCollection>;

export type Column = TableColumns<
  Pick<
    Row,
    | 'name'
    | 'description'
    | 'parent_name'
    | 'created_at'
    | 'created_by_full_name'
    | 'modified_at'
    | 'modified_by_full_name'
  >
>;

export const tableStorageKey = 'priceLists_dataTable';

export function getColumnHeadersMap(
  t: TFunction
): TableColumnHeadersMap<Column> {
  return {
    no: t('No.'),
    name: t('Name'),
    description: t('Description'),
    parent_name: t('Base price list'),
    created_at: t('Created at'),
    created_by_full_name: t('Created by'),
    modified_at: t('Modified at'),
    modified_by_full_name: t('Modified by'),
  };
}

export function additionalColumnProperties(
  t: TFunction,
  column: keyof ReturnType<typeof getColumnHeadersMap> | 'actions',
  setContextMenuSelection: Dispatch<SetStateAction<any>>,
  setAction: (t: string) => void,
  readGuard: boolean,
  pricesPageEpGuard: boolean,
  deleteGuard: boolean
) {
  switch (column) {
    case 'no':
      return {
        noColumnStyle,
        style: {
          width: 30,
          maxWidth: 30,
        },
        bodyStyle: {
          width: 30,
          maxWidth: 30,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'name':
    case 'parent_name':
    case 'created_by_full_name':
    case 'modified_by_full_name':
      return {
        style: { width: 250 },
        bodyStyle: { width: 250 },
      };

    case 'description':
      return {
        body: (row: Row) =>
          !!row[column] ? (
            <Tooltip text={row[column]}>
              {withTrailingEllipsis(row[column]!, 0, 34)}
            </Tooltip>
          ) : (
            emptyCell
          ),
        style: { width: 300 },
        bodyStyle: {
          width: 300,
        },
      };

    case 'created_at':
    case 'modified_at':
      return {
        body: (row: any) => dateCell(row[column], true),
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'actions':
      return {
        body: (row: Row) => (
          <ActionsTableMenu
            actions={[
              {
                label: t('View Details'),
                icon: 'fas fa-search',
                disabled: !readGuard,
                command: () => {
                  setContextMenuSelection(row);
                  setAction('view-details');
                },
              },
              {
                label: t('Edit price list'),
                icon: 'fas fa-edit',
                disabled: !readGuard,
                command: () => {
                  setContextMenuSelection(row);
                  setAction('edit');
                },
              },
              {
                label: t('Price definitions'),
                icon: 'fas fa-tag',
                disabled: !pricesPageEpGuard,
                command: () => {
                  setContextMenuSelection(row);
                  setAction('price-definitions');
                },
              },
              {
                label: t('Print price list'),
                icon: 'fas fa-print',
                disabled: true,
                command: () => {
                  setContextMenuSelection(row);
                  setAction('print-price');
                },
              },
              {
                label: t('Delete price list'),
                icon: 'fas fa-trash',
                disabled: !deleteGuard,
                command: () => {
                  setContextMenuSelection(row);
                  setAction('delete');
                },
              },
            ]}
          />
        ),
      };
  }
}
